import React, { useState } from 'react';
import { Mail, Lock, Phone, User, MapPin, Calendar, Zap, ChevronDown, Check, X, CheckCircle, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { studentSignup } from '../../services/api';
import TermsModal from './TermsModal'; 

// Success Popup Component
const SuccessPopup = ({ isDarkMode, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`max-w-md w-full rounded-2xl shadow-2xl p-8 text-center animate-in zoom-in ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="w-20 h-20 mx-auto mb-6 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
          <CheckCircle className="w-12 h-12 text-white" />
        </div>
        
        <h2 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Account Created Successfully!
        </h2>
        
        <p className={`text-base mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          We've sent a verification email to your inbox. Please check your email and click the verification link to activate your account.
        </p>
        
        <div className={`p-4 rounded-lg mb-6 ${
          isDarkMode ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'
        }`}>
          <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
            📧 <strong>Check your spam folder</strong> if you don't see the email within a few minutes.
          </p>
        </div>
        
        <button
          onClick={onClose}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold py-3 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all"
        >
          Got it!
        </button>
      </div>
    </div>
  );
};

export default function Student({ isDarkMode }) {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    countryCode: '+91',
    mobile: '',
    dob: '',
    serviceInterest: '',
    city: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  
  // Password Visibility State
  const [showPassword, setShowPassword] = useState(false);
  
  // Terms State
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // --- BASIC VALIDATION ---
    if (!formData.fullName.trim()) {
      setError('Full name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    // --- STRICT PASSWORD VALIDATION ---
    const password = formData.password;
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (!hasUpperCase) {
      setError('Password must contain at least one uppercase letter (A-Z).');
      return;
    }
    if (!hasLowerCase) {
      setError('Password must contain at least one lowercase letter (a-z).');
      return;
    }
    if (!hasNumber) {
      setError('Password must contain at least one number (0-9).');
      return;
    }
    if (!hasSpecialChar) {
      setError('Password must contain at least one special character (!@#$...).');
      return;
    }
    
    // --- END PASSWORD VALIDATION ---

    if (!formData.serviceInterest) {
      setError('Please select your service interest');
      return;
    }
    if (!termsAccepted) {
      setError('You must accept the Terms & Conditions');
      return;
    }

    setIsSubmitting(true);

    try {
      const userData = {
        fullName: formData.fullName,
        email: formData.email,
        password: formData.password,
        mobile: formData.mobile ? formData.countryCode + formData.mobile : undefined,
        countryCode: formData.countryCode,
        dob: formData.dob || undefined,
        city: formData.city || undefined,
        serviceInterest: formData.serviceInterest,
      };

      await studentSignup(userData);

      setShowSuccessPopup(true);

      setFormData({
        fullName: '',
        email: '',
        password: '',
        countryCode: '+91',
        mobile: '',
        dob: '',
        serviceInterest: '',
        city: '',
      });
      setTermsAccepted(false); 

    } catch (err) {
      console.error('Signup Error:', err);
      if (err.response?.status === 409) {
        setError('An account with this email already exists. Please login instead.');
      } else {
        setError(err.response?.data?.error || err.response?.data?.message || 'Signup failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.fullName && formData.email && 
                      formData.password && formData.serviceInterest;

  return (
    <>
      <TermsModal 
        isOpen={isTermsOpen} 
        onClose={() => setIsTermsOpen(false)} 
        onAgree={() => {
          setTermsAccepted(true);
          setIsTermsOpen(false);
        }}
        isDarkMode={isDarkMode} 
      />

      {showSuccessPopup && (
        <SuccessPopup 
          isDarkMode={isDarkMode} 
          onClose={() => {
            setShowSuccessPopup(false);
            window.location.href = '/login?type=student';
          }} 
        />
      )}

      {error && (
        <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm flex items-start gap-2 animate-in slide-in-from-top-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
        {/* Full Name */}
        <div className="md:col-span-2">
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Full Name <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              name="fullName"
              required
              value={formData.fullName}
              onChange={handleInputChange}
              className={`w-full pl-11 pr-4 py-3.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="Enter your full name"
            />
          </div>
        </div>

        {/* Email */}
        <div className="md:col-span-2">
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Email Address <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full pl-11 pr-4 py-3.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="your.email@example.com"
            />
          </div>
        </div>

        {/* Password - UPDATED WITH STRICT REQUIREMENTS */}
        <div className="md:col-span-2">
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Password <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              required
              value={formData.password}
              onChange={handleInputChange}
              className={`w-full pl-11 pr-12 py-3.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="Create a strong password"
            />
            
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full transition-colors ${
                isDarkMode 
                  ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' 
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          
          {/* Helper Text for Password Rules */}
          <p className={`mt-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character.
          </p>
        </div>

        {/* Service Interest */}
        <div className="md:col-span-2">
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Interested In <span className="text-red-500">*</span>
          </label>
          <div className="relative group">
            <Zap className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <select
              name="serviceInterest"
              required
              value={formData.serviceInterest}
              onChange={handleInputChange}
              className={`w-full pl-11 pr-10 py-3.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="">Select a service</option>
              <option value="admission">🎓 Admission</option>
              <option value="counseling">💬 Counseling</option>
              <option value="coaching_prep">📚 Coaching and Prep</option>
              <option value="training_certification">🏆 Training and Certification</option>
            </select>
            <ChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 pointer-events-none ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
          </div>
        </div>

        {/* Mobile Number */}
        <div className="md:col-span-2">
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Mobile Number <span className="text-gray-400 text-xs">(Optional)</span>
          </label>
          
          <div className={`flex items-center rounded-lg border-2 overflow-hidden transition-all ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20' 
              : 'bg-white border-gray-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20'
          }`}>
            
            <button
              type="button"
              className={`flex items-center gap-2 px-3 py-3.5 ${
                isDarkMode ? 'text-white hover:bg-gray-700' : 'text-gray-900 hover:bg-gray-50'
              }`}
            >
              <span className="text-xl">🇮🇳</span>
              <span className="font-semibold">{formData.countryCode}</span>
              <div className={`w-px h-5 ml-1 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
            </button>

            <div className="relative flex-1">
              <Phone className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                className={`w-full pl-11 pr-4 py-3.5 bg-transparent border-none outline-none focus:ring-0 ${
                  isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
                }`}
                placeholder="9876543210"
              />
            </div>
          </div>
        </div>

        {/* DOB */}
        <div>
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Date of Birth <span className="text-gray-400 text-xs">(Optional)</span>
          </label>
          <div className="relative group">
            <Calendar className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="date"
              name="dob"
              value={formData.dob}
              onChange={handleInputChange}
              className={`w-full pl-11 pr-4 py-3.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>
        </div>

        {/* City */}
        <div>
          <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            City <span className="text-gray-400 text-xs">(Optional)</span>
          </label>
          <div className="relative group">
            <MapPin className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              className={`w-full pl-11 pr-4 py-3.5 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="Enter your city"
            />
          </div>
        </div>
      </div>

      {/* Terms & Conditions Checkbox */}
      <div className="mt-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            required
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            I agree to the{' '}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setIsTermsOpen(true);
              }}
              className="text-blue-600 hover:text-blue-700 font-semibold hover:underline focus:outline-none"
            >
              Terms & Conditions
            </button>
          </span>
        </label>
      </div>

      {/* Submit Button */}
      <button
        type="button"
        disabled={!isFormValid || isSubmitting || !termsAccepted}
        onClick={handleSubmit}
        className="w-full mt-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold py-4 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>Creating Account...</span>
          </>
        ) : (
          <>
            <Check className="w-5 h-5" />
            <span>Create Account</span>
          </>
        )}
      </button>
    </>
  );
}